import Link from 'next/link'
import { ArrowRight, Sparkles } from 'lucide-react'
import { motion } from 'motion/react'

import { Button } from '@/components/ui/button'

import { talentPoolCopy } from '@/app/talent-pool/copy'

type TalentPoolFinalCtaFooterSectionProps = {
  onScrollToUpload: () => void
}

export function TalentPoolFinalCtaFooterSection({
  onScrollToUpload,
}: TalentPoolFinalCtaFooterSectionProps) {
  const copy = talentPoolCopy

  return (
    <>
      <section className="pb-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border border-border-primary bg-gradient-to-br from-brand-primary/5 via-purple-50/50 to-blue-50/50 dark:from-brand-primary/10 dark:via-purple-950/20 dark:to-blue-950/20 px-6 py-16 text-center sm:px-8 sm:py-20"
        >
          {/* Decorative elements */}
          <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-brand-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-purple-500/10 blur-3xl" />

          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-border-primary bg-white dark:bg-card shadow-lg"
            >
              <Sparkles className="h-8 w-8 text-brand-primary" />
            </motion.div>

            <h2 className="text-[32px] font-medium leading-[40px] tracking-[-0.02em] text-text-heading md:text-[48px] md:leading-[56px] md:tracking-[-0.04em]">
              {copy.landing.finalCtaTitleLineOne}
              <br />
              <span className="inline-flex items-center bg-status-success-bg px-1">
                {copy.landing.finalCtaTitleLineTwo}
              </span>
            </h2>

            <p className="mx-auto mt-5 max-w-[560px] text-[16px] leading-[26px] text-text-muted md:text-[20px] md:leading-[30px]">
              {copy.landing.finalCtaDescription}
            </p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Button
                type="button"
                onClick={onScrollToUpload}
                className="mt-8 h-12 rounded-xl bg-brand-primary px-8 text-base font-semibold text-white shadow-button hover:bg-brand-primary-hover hover:shadow-lg md:h-14 md:px-10 md:text-lg"
              >
                {copy.landing.finalCtaButton}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-border-primary pb-6 pt-10">
        <nav
          aria-label={copy.footer.exploreLabel}
          className="mx-auto mb-8 max-w-3xl"
        >
          <h2 className="mb-3 text-center text-xs font-semibold uppercase tracking-widest text-text-muted">
            {copy.footer.exploreLabel}
          </h2>
          <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            {copy.footer.links.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="text-sm text-text-muted transition-colors hover:text-brand-primary"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-col items-center gap-3 border-t border-border-primary pt-6">
          <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            {copy.footer.legalLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="text-sm text-text-muted transition-colors hover:text-brand-primary"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-sm text-text-muted">{copy.footer.copyright}</p>
        </div>
      </footer>
    </>
  )
}
